import { Clear, Tune } from "@mui/icons-material";
import { Button, ClickAwayListener, Divider, Fade, Paper, Popper, Stack, Typography } from "@mui/material";
import { useMemo, useState } from "react";
import { ORDER_SOURCE_TYPE, orderStatusFormatted, RETURN_REFUND_OPTIONS } from "src/constant";
import useGetDataFromSelect from "src/hooks/useGetDataFromSelect";
import useWareHouseList from "src/hooks/warehouse/useWareHouseList";
import BtnGroupDynamic from "src/components/shared/BtnGroupDynamic";

/**
 * Bộ lọc dành riêng cho trang quản lý kho (xuất / nhập).
 * @param {"inventory-out"|"inventory-in"} variant
 */
export default function WarehouseFilter({ setParamsQuery, paramsQuery, variant = "inventory-out" }) {
  const isInventoryIn = variant === "inventory-in";

  const { data: warehouseList } = useWareHouseList({ limit: 100, skip: 0 });
  const { data: providerList } = useGetDataFromSelect(isInventoryIn ? "/api/v1/providers" : null, isInventoryIn ? { limit: 100, skip: 0 } : null);

  const [popperProps, setPopperProps] = useState({
    open: false,
    anchorEl: null,
    placement: "bottom-end",
  });

  const handlePickOptionSearch = ({ name, value }) => {
    if (isInventoryIn && value === ORDER_SOURCE_TYPE.All && (name === "fromWarehouse" || name === "provider")) {
      setParamsQuery({
        ...paramsQuery,
        fromWarehouse: undefined,
        provider: undefined,
        incomingFromSource: name === "fromWarehouse" ? "Warehouse" : "Provider",
      });
      return;
    }

    const updatedParams = {
      ...paramsQuery,
      [name]: value === ORDER_SOURCE_TYPE.All ? undefined : value,
    };

    if (isInventoryIn) {
      if (name === "provider") {
        updatedParams.fromWarehouse = undefined;
      } else if (name === "fromWarehouse") {
        updatedParams.provider = undefined;
      }

      if ((name === "fromWarehouse" || name === "provider") && value !== ORDER_SOURCE_TYPE.All) {
        delete updatedParams.incomingFromSource;
      }
    }

    setParamsQuery(updatedParams);
  };

  const isTatCaActiveForFilterGroup = (groupName) => {
    if (!isInventoryIn || (groupName !== "fromWarehouse" && groupName !== "provider")) {
      return [ORDER_SOURCE_TYPE.All, undefined]?.includes(paramsQuery?.[groupName]);
    }

    if (groupName === "fromWarehouse") {
      if (paramsQuery?.fromWarehouse) return false;
      if (paramsQuery?.provider) return false;
      if (paramsQuery?.incomingFromSource === "Warehouse") return true;
      if (paramsQuery?.incomingFromSource === "Provider") return false;
      return true;
    }

    if (groupName === "provider") {
      if (paramsQuery?.provider) return false;
      if (paramsQuery?.fromWarehouse) return false;
      if (paramsQuery?.incomingFromSource === "Provider") return true;
      if (paramsQuery?.incomingFromSource === "Warehouse") return false;
      return true;
    }

    return [ORDER_SOURCE_TYPE.All, undefined]?.includes(paramsQuery?.[groupName]);
  };

  const statusInFilter = {
    id: "warehouseIn",
    name: "statusIn",
    title: "Trạng thái",
    data: [
      { _id: orderStatusFormatted.Pending, name: "Chờ nhập kho" },
      { _id: orderStatusFormatted.Imported, name: "Đã nhập kho" },
    ],
  };

  const paymentMethodFilter = {
    id: "paymentMethod",
    name: "paymentMethod",
    title: "Hình thức thanh toán",
    comp: (
      <BtnGroupDynamic
        handlePickOptionSearch={handlePickOptionSearch}
        paramsQuery={paramsQuery}
        path="/api/v1/payment-methods"
        name="paymentMethod"
        params={{ limit: 100, skip: 0 }}
      />
    ),
  };

  const filterList = isInventoryIn
    ? [
        statusInFilter,
        {
          id: "fromWarehouse",
          name: "fromWarehouse",
          title: "Kho xuất",
          data: Array.isArray(warehouseList?.data) ? warehouseList.data : [],
        },
        {
          id: "provider",
          name: "provider",
          title: "NCC",
          data: Array.isArray(providerList?.data) ? providerList.data : [],
        },
        paymentMethodFilter,
        {
          id: "statusOut",
          name: "type",
          title: "Hình thức",
          data: [
            { _id: RETURN_REFUND_OPTIONS.NEW, name: "Nhập mới" },
            { _id: RETURN_REFUND_OPTIONS.RETURN, name: "Hàng trả" },
            { _id: RETURN_REFUND_OPTIONS.Remove, name: "Xuất bỏ" },
          ],
        },
      ]
    : [
        statusInFilter,
        paymentMethodFilter,
        {
          id: "toWarehouse",
          name: "toWarehouse",
          title: "Kho nhập",
          data: Array.isArray(warehouseList?.data) ? warehouseList.data : [],
        },
      ];

  const handleSetPop = (placement) => (event) => {
    setPopperProps({
      open: !popperProps.open,
      anchorEl: event.currentTarget,
      placement,
    });
  };

  const handleClosePopper = () => {
    setPopperProps((prev) => ({ ...prev, open: false }));
  };

  const hasActiveFilters = useMemo(() => {
    return (
      paramsQuery &&
      typeof paramsQuery === "object" &&
      Object.keys(paramsQuery).some((k) => {
        const val = paramsQuery[k];
        return val !== undefined && val !== null && val !== "";
      })
    );
  }, [paramsQuery]);

  const handleClearFilters = () => {
    setParamsQuery({});
  };

  return (
    <ClickAwayListener onClickAway={handleClosePopper}>
      <Stack sx={{ display: "inline-block" }}>
        <Button
          size="small"
          variant="outlined"
          sx={{ height: "100%", maxHeight: "36.5px" }}
          startIcon={<Tune />}
          onClick={handleSetPop("bottom-end")}
        >
          Lọc
        </Button>
        <Popper
          disablePortal
          sx={{
            zIndex: 1200,
            maxWidth: "900px",
            width: "90%",
            maxHeight: "330px",
            height: "auto",
            overflow: "auto",
            boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
          }}
          open={popperProps.open}
          anchorEl={popperProps.anchorEl}
          placement={popperProps.placement}
          transition
        >
          {({ TransitionProps }) => (
            <Fade
              {...TransitionProps}
              timeout={350}
            >
              <Paper sx={{ p: "12px", display: "flex", flexDirection: "column" }}>
                <Stack
                  direction="row"
                  justifyContent="flex-end"
                  sx={{ pb: 1, mb: 1, borderBottom: "1px solid var(--light-grey)" }}
                >
                  <Button
                    size="small"
                    variant="outlined"
                    color="inherit"
                    startIcon={<Clear />}
                    disabled={!hasActiveFilters}
                    onClick={handleClearFilters}
                    sx={{ fontSize: "12px" }}
                  >
                    Xóa bộ lọc
                  </Button>
                </Stack>
                {filterList.map((v) => (
                  <Stack
                    direction="column"
                    key={v.id}
                    spacing={1}
                    mb={1.5}
                    pd={1.5}
                  >
                    <Typography sx={{ fontWeight: "bold", fontSize: "12px" }}>{v.title}</Typography>
                    <Stack
                      direction="row"
                      gap={1}
                    >
                      <Button
                        onClick={() =>
                          handlePickOptionSearch({
                            name: v.name,
                            value: ORDER_SOURCE_TYPE.All,
                          })
                        }
                        sx={{
                          border: isTatCaActiveForFilterGroup(v.name) ? "1px solid var(--confirm-txt)" : "1px solid var(--light-grey)",
                          color: isTatCaActiveForFilterGroup(v.name) ? "var(--confirm-txt)" : "var(--black)",
                          display: "inline",
                          borderRadius: "4px",
                          fontSize: "12px",
                        }}
                      >
                        Tất cả
                      </Button>
                      {!v?.comp &&
                        v?.data?.map((val) => (
                          <Button
                            key={val?._id}
                            onClick={() =>
                              handlePickOptionSearch({
                                name: v?.name,
                                value: val?._id,
                              })
                            }
                            sx={{
                              border: [val?._id]?.includes(paramsQuery?.[v?.name]) ? "1px solid var(--confirm-txt)" : "1px solid var(--light-grey)",
                              color: [val?._id]?.includes(paramsQuery?.[v?.name]) ? "var(--confirm-txt)" : "var(--black)",
                              display: "inline",
                              p: "6px 12px !important",
                              borderRadius: "4px",
                              fontSize: "12px",
                            }}
                          >
                            {val?.name}
                          </Button>
                        ))}
                      {v?.comp ?? ""}
                    </Stack>
                    <Divider sx={{ color: "var(--light-grey)" }} />
                  </Stack>
                ))}
              </Paper>
            </Fade>
          )}
        </Popper>
      </Stack>
    </ClickAwayListener>
  );
}
